import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home, AlertCircle } from 'lucide-react';
import { centralizedErrorHandler } from '@/utils/errorHandling/centralizedErrorHandler';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    retryCount: 0
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[GlobalErrorBoundary] Uncaught error:', error, errorInfo);
    
    this.setState({ errorInfo });
    
    // Log error to centralized error handler
    centralizedErrorHandler.logError(error, {
      operation: 'глобальная обработка ошибок',
      component: 'GlobalErrorBoundary',
      metadata: {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
        retryCount: this.state.retryCount
      }
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleGoHome = (): void => {
    window.location.href = '/';
  };

  private handleReportError = (): void => {
    const errorReport = {
      message: this.state.error?.message || 'Неизвестная ошибка',
      stack: this.state.error?.stack || 'Стек вызовов недоступен',
      componentStack: this.state.errorInfo?.componentStack || 'Компонент-стек недоступен',
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      retryCount: this.state.retryCount
    };

    // Log detailed error report
    centralizedErrorHandler.logError(new Error('Пользователь отправил отчет об ошибке'), {
      operation: 'отчет об ошибке',
      component: 'GlobalErrorBoundary',
      metadata: errorReport
    });

    // You can also send this to your error reporting service
    console.log('Error report generated:', errorReport);
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isRecoverableError = this.state.retryCount < 3;
      const errorMessage = this.state.error?.message || 'Неизвестная ошибка';
      const isNetworkError = errorMessage.includes('fetch') || errorMessage.includes('network');

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <Card className="max-w-2xl w-full">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className="h-12 w-12 text-destructive" />
              </div>
              <CardTitle className="text-2xl">
                Что-то пошло не так
              </CardTitle>
              <CardDescription>
                {isNetworkError 
                  ? 'Проблема с подключением к сети. Проверьте интернет-соединение.'
                  : 'Произошла непредвиденная ошибка при работе приложения.'
                }
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Подробности ошибки</AlertTitle>
                <AlertDescription className="mt-2">
                  <details className="space-y-2">
                    <summary className="cursor-pointer font-medium">
                      {errorMessage}
                    </summary>
                    <div className="mt-2 p-2 bg-gray-100 rounded text-xs font-mono max-h-32 overflow-y-auto">
                      {this.state.error?.stack}
                    </div>
                  </details>
                </AlertDescription>
              </Alert>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {isRecoverableError && (
                  <Button onClick={this.handleRetry} className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Попробовать снова
                  </Button>
                )}
                
                <Button variant="outline" onClick={this.handleReload} className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Перезагрузить страницу
                </Button>
                
                <Button variant="outline" onClick={this.handleGoHome} className="flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  На главную
                </Button>
              </div>

              <div className="text-center">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={this.handleReportError}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Отправить отчет об ошибке
                </Button>
              </div>

              <div className="text-xs text-muted-foreground text-center space-y-1">
                <p>Попытка восстановления: {this.state.retryCount}/3</p>
                <p>Время: {new Date().toLocaleString()}</p>
                <p>ID сессии: {Date.now()}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
